"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { keepPreviousData, type InfiniteData, type UseInfiniteQueryResult, useInfiniteQuery } from "@tanstack/react-query";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiXTdhContributionsPage } from "@/generated/models/ApiXTdhContributionsPage";
import type { XtdhTokensOrder, XtdhTokensSortField } from "@/hooks/useXtdhTokensQuery";
import { DEFAULT_TOKEN_CONTRIBUTORS_GROUP_BY, DEFAULT_TOKEN_CONTRIBUTORS_SORT_DIRECTION, DEFAULT_TOKEN_CONTRIBUTORS_SORT_FIELD, type XtdhTokenContributorsGroupBy } from "@/components/xtdh/received/constants";
import { SortDirection } from "@/entities/ISort";
import { commonApiFetch } from "@/services/api/common-api";
interface UseXtdhTokenContributorsQueryParams {
  readonly contract?: string | null | undefined;
  readonly tokenId?: number | null | undefined;
  readonly pageSize?: number | undefined;
  readonly sortField?: XtdhTokensSortField | undefined;
  readonly order?: XtdhTokensOrder | undefined;
  readonly groupBy?: XtdhTokenContributorsGroupBy | undefined;
  readonly enabled?: boolean | undefined;
}
type XtdhTokenContributorsInfiniteData = InfiniteData<ApiXTdhContributionsPage>;
type UseXtdhTokenContributorsQueryResult = UseInfiniteQueryResult<XtdhTokenContributorsInfiniteData, Error> & {
  readonly contributors: ApiXTdhContributionsPage["data"];
  readonly errorMessage?: string | undefined;
  readonly isEnabled: boolean;
};
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_STALE_TIME = 30_000;
const SORT_MAP: Record<SortDirection, XtdhTokensOrder> = {
  [SortDirection.ASC]: "ASC",
  [SortDirection.DESC]: "DESC"
};
export function useXtdhTokenContributorsQuery(t0) {
  const $ = _c(33);
  const {
    contract,
    tokenId,
    pageSize: t1,
    sortField: t2,
    order: t3,
    groupBy: t4,
    enabled: t5
  } = t0;
  const pageSize = t1 === undefined ? DEFAULT_PAGE_SIZE : t1;
  const sortField = t2 === undefined ? DEFAULT_TOKEN_CONTRIBUTORS_SORT_FIELD : t2;
  const order = t3 === undefined ? SORT_MAP[DEFAULT_TOKEN_CONTRIBUTORS_SORT_DIRECTION] : t3;
  const groupBy = t4 === undefined ? DEFAULT_TOKEN_CONTRIBUTORS_GROUP_BY : t4;
  const enabled = t5 === undefined ? true : t5;
  let t6;
  if ($[0] !== contract) {
    t6 = contract?.trim() ?? "";
    $[0] = contract;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const normalizedContract = t6;
  let t7;
  if ($[2] !== tokenId) {
    t7 = Number.isFinite(tokenId) ? Math.trunc(Number(tokenId)).toString() : "";
    $[2] = tokenId;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const normalizedTokenId = t7;
  const hasRequiredFilters = Boolean(normalizedContract) && Boolean(normalizedTokenId);
  const isEnabled = hasRequiredFilters && enabled;
  let t8;
  if ($[4] !== pageSize) {
    t8 = Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : DEFAULT_PAGE_SIZE;
    $[4] = pageSize;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const normalizedPageSize = t8;
  const normalizedOrder = order === "ASC" ? "ASC" : "DESC";
  let t9;
  if ($[6] !== normalizedContract) {
    t9 = normalizedContract.toLowerCase();
    $[6] = normalizedContract;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== groupBy || $[9] !== normalizedOrder || $[10] !== normalizedPageSize || $[11] !== normalizedTokenId || $[12] !== sortField || $[13] !== t9) {
    t10 = [QueryKey.XTDH_TOKEN_CONTRIBUTORS, t9, normalizedTokenId, normalizedPageSize, sortField, normalizedOrder, groupBy];
    $[8] = groupBy;
    $[9] = normalizedOrder;
    $[10] = normalizedPageSize;
    $[11] = normalizedTokenId;
    $[12] = sortField;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const queryKey = t10;
  let t11;
  if ($[15] !== groupBy || $[16] !== normalizedContract || $[17] !== normalizedOrder || $[18] !== normalizedPageSize || $[19] !== normalizedTokenId || $[20] !== sortField) {
    t11 = async t12 => {
      const {
        pageParam
      } = t12;
      const currentPage = pageParam ?? DEFAULT_PAGE;
      const params = {
        page: currentPage.toString(),
        page_size: normalizedPageSize.toString(),
        sort: sortField,
        order: normalizedOrder,
        group_by: groupBy
      };
      return commonApiFetch({
        endpoint: `xtdh/tokens/${normalizedContract}/${normalizedTokenId}/contributors`,
        params
      });
    };
    $[15] = groupBy;
    $[16] = normalizedContract;
    $[17] = normalizedOrder;
    $[18] = normalizedPageSize;
    $[19] = normalizedTokenId;
    $[20] = sortField;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== isEnabled || $[23] !== queryKey || $[24] !== t11) {
    t12 = {
      queryKey,
      queryFn: t11,
      initialPageParam: DEFAULT_PAGE,
      getNextPageParam: _temp,
      enabled: isEnabled,
      staleTime: DEFAULT_STALE_TIME,
      placeholderData: keepPreviousData
    };
    $[22] = isEnabled;
    $[23] = queryKey;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const query = useInfiniteQuery(t12);
  let t13;
  if ($[26] !== query.data?.pages) {
    t13 = query.data?.pages.flatMap(_temp2) ?? [];
    $[26] = query.data?.pages;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const contributors = t13;
  const errorMessage = query.error instanceof Error ? query.error.message : undefined;
  let t14;
  if ($[28] !== contributors || $[29] !== errorMessage || $[30] !== isEnabled || $[31] !== query) {
    t14 = {
      ...query,
      contributors,
      errorMessage,
      isEnabled
    };
    $[28] = contributors;
    $[29] = errorMessage;
    $[30] = isEnabled;
    $[31] = query;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
}
function _temp2(pageData) {
  return pageData.data;
}
function _temp(lastPage) {
  return lastPage.next ? lastPage.page + 1 : undefined;
}